import { useState } from "react";
import { LuChevronDown } from "react-icons/lu";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Field } from "../ui/field";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import type { Chapter } from "../../types";
import { PanelHeader } from "./PanelHeader";
import { ExtractToolbar } from "./ExtractToolbar";
import { EmptyState } from "../ui/empty-state";
import { IconOpenIn, IconRemove } from "./icons";
import { cn } from "../../lib/utils";
import {
  TIMELINE_BEAT_KINDS,
  TIMELINE_BEAT_KIND_LABELS,
  TIMELINE_DATE_PRECISIONS,
  TIMELINE_NARRATIVE_POSITIONS,
  beatsInChapter,
  type BookTimelineBeat,
  type TimelineBeatKind,
  type TimelineDatePrecision,
  type TimelineNarrativePosition,
} from "../../../shared/timelineRoster";

function ChapterBeatCard({
  beat,
  onUpdateBeat,
  onOpenInGuide,
  onRequestRemove,
  expanded,
  onToggle,
}: {
  beat: BookTimelineBeat;
  onUpdateBeat: (id: string, patch: Partial<BookTimelineBeat>) => void;
  onOpenInGuide: (id: string) => void;
  onRequestRemove: (beat: BookTimelineBeat) => void;
  expanded: boolean;
  onToggle: () => void;
}) {
  const summary = beat.summary.trim() || "Untitled beat";
  const when = beat.when.trim() || beat.sortKey.trim() || "Timing not set";

  return (
    <article
      className={cn(
        "shrink-0 overflow-hidden rounded-xl border bg-card shadow-xs transition-colors",
        expanded ? "border-primary/35 ring-1 ring-primary/10" : "border-border hover:border-foreground/20"
      )}
    >
      <div className="flex items-start gap-2 p-3">
        <Button
          type="button"
          variant="ghost"
          className="h-auto min-w-0 flex-1 justify-start gap-3 px-1 py-0.5 text-left hover:bg-transparent"
          aria-expanded={expanded}
          onClick={onToggle}
        >
          <LuChevronDown
            className={cn(
              "mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform",
              expanded && "rotate-180"
            )}
            aria-hidden
          />
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="line-clamp-2 text-sm font-semibold leading-snug text-foreground">
              {summary}
            </span>
            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs font-normal text-muted-foreground">
              <span>{when}</span>
              <span aria-hidden>·</span>
              <span className="capitalize">{beat.narrativePosition}</span>
            </span>
          </span>
        </Button>

        <div className="flex shrink-0 items-center gap-1">
          <Badge variant="secondary" className="hidden sm:inline-flex">
            {TIMELINE_BEAT_KIND_LABELS[beat.beatKind]}
          </Badge>
          <Button
            size="icon-sm"
            variant="ghost"
            type="button"
            aria-label="Open in Timeline"
            title="Open in Timeline"
            onClick={() => onOpenInGuide(beat.id)}
          >
            <IconOpenIn />
          </Button>
          <Button
            size="icon-sm"
            variant="ghost"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            type="button"
            aria-label="Remove beat"
            title="Remove beat"
            onClick={() => onRequestRemove(beat)}
          >
            <IconRemove />
          </Button>
        </div>
      </div>

      {expanded ? (
        <div className="grid gap-4 border-t border-border bg-muted/20 px-4 py-4">
          <Field label="Summary">
            <Textarea
              aria-label="Beat summary"
              className="min-h-20 resize-y bg-background"
              value={beat.summary}
              rows={2}
              onChange={(e) => onUpdateBeat(beat.id, { summary: e.target.value })}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3 max-[720px]:grid-cols-1">
            <Field label="When" hint="Plain-language time cue">
              <Input
                aria-label="Beat when"
                className="bg-background"
                value={beat.when}
                onChange={(e) => onUpdateBeat(beat.id, { when: e.target.value })}
              />
            </Field>
            <Field label="Sort key" hint="Optional chronological key">
              <Input
                aria-label="Beat sort key"
                className="bg-background"
                placeholder="YYYY-MM-DD"
                value={beat.sortKey}
                onChange={(e) => onUpdateBeat(beat.id, { sortKey: e.target.value })}
              />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3 max-[720px]:grid-cols-1">
            <Field label="Kind">
              <Select
                value={beat.beatKind}
                onValueChange={(value) =>
                  onUpdateBeat(beat.id, { beatKind: value as TimelineBeatKind })
                }
              >
                <SelectTrigger size="sm" aria-label="Beat kind" className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMELINE_BEAT_KINDS.map((kind) => (
                    <SelectItem key={kind} value={kind}>
                      {TIMELINE_BEAT_KIND_LABELS[kind]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Precision">
              <Select
                value={beat.datePrecision}
                onValueChange={(value) =>
                  onUpdateBeat(beat.id, { datePrecision: value as TimelineDatePrecision })
                }
              >
                <SelectTrigger size="sm" aria-label="Date precision" className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMELINE_DATE_PRECISIONS.map((precision) => (
                    <SelectItem key={precision} value={precision}>
                      {precision}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Position">
              <Select
                value={beat.narrativePosition}
                onValueChange={(value) =>
                  onUpdateBeat(beat.id, {
                    narrativePosition: value as TimelineNarrativePosition,
                  })
                }
              >
                <SelectTrigger size="sm" aria-label="Narrative position" className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMELINE_NARRATIVE_POSITIONS.map((position) => (
                    <SelectItem key={position} value={position}>
                      {position}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Chapter note" hint="Optional context specific to this chapter">
            <Textarea
              aria-label="Beat chapter note"
              className="min-h-20 resize-y bg-background"
              placeholder="Chapter-local detail…"
              value={beat.note ?? ""}
              rows={2}
              onChange={(e) => onUpdateBeat(beat.id, { note: e.target.value })}
            />
          </Field>

          {beat.evidence ? (
            <p className="rounded-md border border-border bg-background px-3 py-2 text-xs leading-relaxed text-muted-foreground">
              <span className="font-semibold text-foreground">Evidence:</span> {beat.evidence}
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export function ChapterTimelinePanel({
  activeChapter,
  roster,
  busy,
  extractModel,
  onExtractModelChange,
  openAiModels,
  anthropicModels,
  onExtract,
  onUpdateBeat,
  onAddBeat,
  onOpenInGuide,
  onRequestRemove,
}: {
  activeChapter: Chapter;
  roster: BookTimelineBeat[];
  busy: boolean;
  extractModel: string;
  onExtractModelChange: (model: string, provider: "openai" | "anthropic") => void;
  openAiModels: ReadonlyArray<{ value: string; label: string }>;
  anthropicModels: ReadonlyArray<{ value: string; label: string }>;
  onExtract: () => void;
  onUpdateBeat: (id: string, patch: Partial<BookTimelineBeat>) => void;
  onAddBeat: () => void;
  onOpenInGuide: (id: string) => void;
  onRequestRemove: (beat: BookTimelineBeat) => void;
}) {
  const inChapter = beatsInChapter(roster, activeChapter.id);
  const [expandedBeatId, setExpandedBeatId] = useState<string | null>(null);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PanelHeader
        title="Major beats in this chapter"
        description="Pivotal plot, action, and character changes only — not every time reference."
        className="px-5 py-4"
        actions={
          <ExtractToolbar
            model={extractModel}
            onModelChange={onExtractModelChange}
            openAiModels={openAiModels}
            anthropicModels={anthropicModels}
            onExtract={onExtract}
            extractBusy={busy}
            extractLabel="Extract timeline"
            onAdd={onAddBeat}
            addLabel="Add beat"
            modelClassName="w-40"
          />
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-5 py-4">
        {inChapter.length === 0 ? (
          <EmptyState
            title="No major beats linked to this chapter yet."
            description="Run Extract timeline to pull pivotal moments from the manuscript, or add one manually."
          />
        ) : (
          inChapter.map((beat) => (
            <ChapterBeatCard
              key={beat.id}
              beat={beat}
              onUpdateBeat={onUpdateBeat}
              onOpenInGuide={onOpenInGuide}
              onRequestRemove={onRequestRemove}
              expanded={expandedBeatId === beat.id}
              onToggle={() =>
                setExpandedBeatId((current) => (current === beat.id ? null : beat.id))
              }
            />
          ))
        )}
      </div>
    </div>
  );
}
